// NANDA SÁ. Système de monogrammes vectoriels
// Charte : monoline · stroke 1.5 · sans fond · viewBox 64x64 · currentColor pour héritage
// Réutilisable partout (site, catalogues PDF, favicons, réseaux sociaux)

import React from "react";

const Base = ({ size = 64, className = "", children, "data-testid": testId, ...rest }) => (
  <svg
    viewBox="0 0 64 64"
    width={size}
    height={size}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    data-testid={testId}
    {...rest}
  >
    {children}
  </svg>
);

/* ─────────── 1. NANDA SÁ. Logo principal du Groupe ─────────── */
/* Monogramme géométrique : lettre N stylisée sous une ligne horizontale (macron du Ā) */
export const NandaSaMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-nanda-sa" {...rest}>
    <circle cx="32" cy="32" r="26" />
    <line x1="20" y1="20" x2="44" y2="20" strokeWidth="2" />
    <path d="M22 44 L22 26 L42 44 L42 26" />
  </Base>
);

/* ─────────── 2. JOHN TĀDO. Monogramme JT élégant ─────────── */
/* J descendant avec courbe raffinée, T croisant en haut, style serif haute couture */
export const JohnTadoMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-john-tado" {...rest}>
    <circle cx="32" cy="32" r="26" />
    {/* T horizontal supérieur */}
    <line x1="18" y1="22" x2="46" y2="22" />
    {/* Descente centrale T */}
    <line x1="32" y1="22" x2="32" y2="34" />
    {/* J : courbe descendante qui remonte */}
    <path d="M32 34 Q32 44 24 44 Q20 44 20 40" />
    {/* Sérif décoratif haut du T (à gauche) */}
    <line x1="18" y1="19" x2="18" y2="25" />
    <line x1="46" y1="19" x2="46" y2="25" />
  </Base>
);

/* ─────────── 3. NANDA SÁ Cosmétiques Naturels. Symbole botanique ─────────── */
/* Feuille allongée avec nervure centrale (référence à la nature) */
export const CosmetiquesMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-cosmetiques" {...rest}>
    <circle cx="32" cy="32" r="26" />
    {/* Feuille : deux courbes symétriques */}
    <path d="M32 14 C22 22 22 42 32 50 C42 42 42 22 32 14 Z" />
    {/* Nervure centrale */}
    <line x1="32" y1="14" x2="32" y2="50" />
    {/* Petites nervures secondaires */}
    <path d="M32 24 L27 28" />
    <path d="M32 24 L37 28" />
    <path d="M32 34 L27 38" />
    <path d="M32 34 L37 38" />
  </Base>
);

/* ─────────── 4. NANDA SÁ. Emblème gastronomie & terroir ─────────── */
/* Épi stylisé : tige verticale avec grains flanquants (blé, mil, épices) */
export const NandaSaFoodMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-nanda-sa-food" {...rest}>
    <circle cx="32" cy="32" r="26" />
    {/* Tige centrale */}
    <line x1="32" y1="18" x2="32" y2="48" />
    {/* Grains gauche (3 paires) */}
    <path d="M32 24 Q26 22 24 26" />
    <path d="M32 30 Q26 28 24 32" />
    <path d="M32 36 Q26 34 24 38" />
    <path d="M32 42 Q26 40 24 44" />
    {/* Grains droite (3 paires) */}
    <path d="M32 24 Q38 22 40 26" />
    <path d="M32 30 Q38 28 40 32" />
    <path d="M32 36 Q38 34 40 38" />
    <path d="M32 42 Q38 40 40 44" />
  </Base>
);

/* ─────────── 5. NANDA SÁ ENERGIA PRO. Symbole solaire moderne ─────────── */
/* Soleil géométrique minimaliste : disque central + 8 rayons courts */
export const EnergiaMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-energia" {...rest}>
    <circle cx="32" cy="32" r="26" />
    {/* Disque solaire central */}
    <circle cx="32" cy="32" r="9" />
    {/* 8 rayons */}
    <line x1="32" y1="12" x2="32" y2="17" strokeWidth="2" />
    <line x1="32" y1="47" x2="32" y2="52" strokeWidth="2" />
    <line x1="12" y1="32" x2="17" y2="32" strokeWidth="2" />
    <line x1="47" y1="32" x2="52" y2="32" strokeWidth="2" />
    <line x1="17.8" y1="17.8" x2="21.3" y2="21.3" strokeWidth="2" />
    <line x1="42.7" y1="42.7" x2="46.2" y2="46.2" strokeWidth="2" />
    <line x1="17.8" y1="46.2" x2="21.3" y2="42.7" strokeWidth="2" />
    <line x1="42.7" y1="21.3" x2="46.2" y2="17.8" strokeWidth="2" />
  </Base>
);

/* ─────────── 6. NANDA SÁ Académie. Savoir & Transmission ─────────── */
/* Livre ouvert stylisé avec lignes de texte suggérées */
export const AcademieMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-academie" {...rest}>
    <circle cx="32" cy="32" r="26" />
    {/* Livre ouvert : deux pages en V inversé */}
    <path d="M18 26 L32 22 L46 26" />
    <path d="M18 26 L18 44 L32 40 L46 44 L46 26" />
    {/* Reliure centrale */}
    <line x1="32" y1="22" x2="32" y2="40" />
    {/* Lignes de texte (suggérées) */}
    <line x1="22" y1="31" x2="29" y2="30" opacity="0.5" />
    <line x1="22" y1="35" x2="29" y2="34" opacity="0.5" />
    <line x1="35" y1="30" x2="42" y2="31" opacity="0.5" />
    <line x1="35" y1="34" x2="42" y2="35" opacity="0.5" />
  </Base>
);

/* ─────────── 7. KĀSĀ. Symbole architectural (optionnel/futur) ─────────── */
/* Maison géométrique : toit + volume + porte centrale */
export const KasaMark = ({ size, className, ...rest }) => (
  <Base size={size} className={className} data-testid="mark-kasa" {...rest}>
    <circle cx="32" cy="32" r="26" />
    {/* Toit */}
    <path d="M18 32 L32 20 L46 32" />
    {/* Volume (murs) */}
    <path d="M22 30 L22 46 L42 46 L42 30" />
    {/* Porte centrale */}
    <path d="M29 46 L29 37 L35 37 L35 46" />
    {/* Barre horizontale au sol (fondation) */}
    <line x1="16" y1="46" x2="48" y2="46" />
  </Base>
);

/* ─────────── Utilitaire : mapper slug → composant ─────────── */
export const MARK_BY_SLUG = {
  "nanda-sa": NandaSaMark,
  "nanda-sa-food": NandaSaFoodMark,
  "john-tado": JohnTadoMark,
  "eleone": CosmetiquesMark,
  "cosmetiques": CosmetiquesMark,
  "energia-pro": EnergiaMark,
  "academie": AcademieMark,
  "kasa": KasaMark,
};

export const BrandMark = ({ slug, ...rest }) => {
  const M = MARK_BY_SLUG[slug];
  return M ? <M {...rest} /> : null;
};
